/**
 * spaceWaveScripts.ts
 * Per-level `WaveSpec` arrays for World 2 levels.
 *
 * Extracted from `spaceLevels.ts` to keep the level-config module under
 * the AGENTS.md 300-line cap. Wave timing data lives here; level gating
 * (asteroids / enemies / boss / endCondition) and the per-level JSDoc
 * tuning notes live in `spaceLevels.ts` next to the `SpaceLevelConfig`
 * objects that consume these arrays.
 *
 * Each array's `atSeconds` values are strictly ascending — the wave
 * scheduler's monotonic cursor requires this, and the assertions in
 * `spaceLevels.test.ts` validate it.
 *
 * @module spaceWaveScripts
 */

import type { WaveSpec } from '../engine/waveScheduler';

/** Level 2-2 — 12 waves over 60s, baseY varied top/mid/bottom so a single lane never works. */
export const SPACE_LEVEL_2_2_WAVE_SCRIPT: ReadonlyArray<WaveSpec> = [
  { atSeconds: 3,  kind: 'fighter',   formation: 'line', count: 3, baseY: 60 },
  { atSeconds: 7,  kind: 'fighter',   formation: 'v',    count: 5, baseY: 200 },
  { atSeconds: 12, kind: 'bomber',    formation: 'line', count: 2, baseY: 135 },
  { atSeconds: 17, kind: 'fighter',   formation: 'arc',  count: 4, baseY: 90 },
  { atSeconds: 22, kind: 'satellite', formation: 'v',    count: 3, baseY: 170 },
  { atSeconds: 27, kind: 'fighter',   formation: 'line', count: 4, baseY: 100 },
  { atSeconds: 32, kind: 'fighter',   formation: 'v',    count: 5, baseY: 210 },
  { atSeconds: 37, kind: 'bomber',    formation: 'v',    count: 3, baseY: 80 },
  { atSeconds: 42, kind: 'fighter',   formation: 'arc',  count: 5, baseY: 135 },
  { atSeconds: 47, kind: 'satellite', formation: 'line', count: 3, baseY: 60 },
  { atSeconds: 52, kind: 'fighter',   formation: 'v',    count: 5, baseY: 200 },
  { atSeconds: 56, kind: 'fighter',   formation: 'arc',  count: 6, baseY: 135 },
];

/** Level 2-3 — 17 waves over 80s, mixed kinds + sub-3s closing burst, baseY spans 60..210 to lock no lane safe. */
export const SPACE_LEVEL_2_3_WAVE_SCRIPT: ReadonlyArray<WaveSpec> = [
  { atSeconds: 3,  kind: 'fighter',   formation: 'v',    count: 5, baseY: 90 },
  { atSeconds: 7,  kind: 'fighter',   formation: 'arc',  count: 5, baseY: 200 },
  { atSeconds: 12, kind: 'bomber',    formation: 'line', count: 3, baseY: 135 },
  { atSeconds: 17, kind: 'satellite', formation: 'arc',  count: 4, baseY: 60 },
  { atSeconds: 22, kind: 'fighter',   formation: 'v',    count: 6, baseY: 170 },
  { atSeconds: 28, kind: 'bomber',    formation: 'v',    count: 3, baseY: 100 },
  { atSeconds: 34, kind: 'fighter',   formation: 'line', count: 5, baseY: 210 },
  { atSeconds: 39, kind: 'satellite', formation: 'line', count: 4, baseY: 75 },
  { atSeconds: 44, kind: 'fighter',   formation: 'arc',  count: 6, baseY: 135 },
  { atSeconds: 49, kind: 'bomber',    formation: 'line', count: 3, baseY: 200 },
  { atSeconds: 53, kind: 'fighter',   formation: 'v',    count: 6, baseY: 70 },
  { atSeconds: 58, kind: 'satellite', formation: 'v',    count: 3, baseY: 150 },
  { atSeconds: 62, kind: 'fighter',   formation: 'arc',  count: 5, baseY: 100 },
  { atSeconds: 66, kind: 'fighter',   formation: 'v',    count: 5, baseY: 210 },
  { atSeconds: 70, kind: 'bomber',    formation: 'v',    count: 3, baseY: 135 },
  { atSeconds: 73, kind: 'fighter',   formation: 'line', count: 5, baseY: 70 },
  { atSeconds: 76, kind: 'fighter',   formation: 'arc',  count: 7, baseY: 180 },
];

/** Level 2-4 — 14 waves over 70s alongside asteroids; closes on a layered Star-Eater fleet. */
export const SPACE_LEVEL_2_4_WAVE_SCRIPT: ReadonlyArray<WaveSpec> = [
  { atSeconds: 4,  kind: 'fighter',   formation: 'line', count: 3, baseY: 80 },
  { atSeconds: 9,  kind: 'fighter',   formation: 'v',    count: 4, baseY: 200 },
  { atSeconds: 14, kind: 'bomber',    formation: 'line', count: 2, baseY: 135 },
  { atSeconds: 19, kind: 'satellite', formation: 'arc',  count: 3, baseY: 70 },
  { atSeconds: 24, kind: 'fighter',   formation: 'arc',  count: 5, baseY: 170 },
  { atSeconds: 29, kind: 'bomber',    formation: 'v',    count: 3, baseY: 100 },
  { atSeconds: 34, kind: 'fighter',   formation: 'line', count: 4, baseY: 210 },
  { atSeconds: 39, kind: 'satellite', formation: 'line', count: 3, baseY: 60 },
  { atSeconds: 44, kind: 'fighter',   formation: 'v',    count: 5, baseY: 135 },
  { atSeconds: 49, kind: 'bomber',    formation: 'line', count: 3, baseY: 200 },
  { atSeconds: 54, kind: 'fighter',   formation: 'arc',  count: 5, baseY: 80 },
  { atSeconds: 59, kind: 'starEater', formation: 'v',    count: 4, baseY: 170 },
  { atSeconds: 63, kind: 'fighter',   formation: 'arc',  count: 5, baseY: 100 },
  { atSeconds: 67, kind: 'starEater', formation: 'v',    count: 5, baseY: 200 },
];

/** Level 2-S — 8 mid-density waves over 90s (fighters + satellites + a bomber), baseY varied for exploration pacing. */
export const SPACE_LEVEL_2_S_WAVE_SCRIPT: ReadonlyArray<WaveSpec> = [
  { atSeconds: 10, kind: 'fighter',   formation: 'line', count: 3, baseY: 80 },
  { atSeconds: 22, kind: 'satellite', formation: 'arc',  count: 3, baseY: 170 },
  { atSeconds: 34, kind: 'fighter',   formation: 'v',    count: 4, baseY: 60 },
  { atSeconds: 45, kind: 'fighter',   formation: 'line', count: 3, baseY: 200 },
  { atSeconds: 56, kind: 'fighter',   formation: 'arc',  count: 4, baseY: 100 },
  { atSeconds: 65, kind: 'satellite', formation: 'line', count: 3, baseY: 135 },
  { atSeconds: 75, kind: 'fighter',   formation: 'v',    count: 4, baseY: 210 },
  { atSeconds: 84, kind: 'fighter',   formation: 'arc',  count: 4, baseY: 90 },
];
